import type { BookResult, Load, LoadList, RateShopResult, RelayConfig } from './types';
import { staticApi } from './lib/staticApi';

/**
 * The static build has no server: fixture data and the rating model run in the
 * browser. Set at build time (VITE_STATIC=1), never at runtime, so the network
 * code is tree-shaken out of the static bundle entirely.
 */
const STATIC_BUILD = import.meta.env.VITE_STATIC === '1';

/** Relay errors arrive as { error, code }; surface the message, not "500". */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`Relay returned non-JSON from ${path}: ${text.slice(0, 200)}`);
  }

  if (!response.ok) {
    const err = body as { error?: string; code?: string } | null;
    const error = new Error(err?.error ?? `${response.status} ${response.statusText}`);
    (error as Error & { code?: string }).code = err?.code;
    throw error;
  }

  return body as T;
}

const networkApi = {
  config: () => request<RelayConfig>('/api/config'),

  loads: () => request<LoadList>('/api/loads'),

  load: (loadId: string) => request<Load>(`/api/loads/${encodeURIComponent(loadId)}`),

  rateShop: (loadId: string) =>
    request<RateShopResult>(`/api/loads/${encodeURIComponent(loadId)}/rateshop`, {
      method: 'POST',
    }),

  /** Rates a load by value - used by the embed view for URL-supplied shipments. */
  rateShopLoad: (load: unknown) =>
    request<RateShopResult>('/api/rateshop', { method: 'POST', body: JSON.stringify({ load }) }),

  /** Books a load by value, for the same reason. */
  bookLoad: (load: unknown, quote: unknown) =>
    request<BookResult>('/api/book', { method: 'POST', body: JSON.stringify({ load, ...(quote as object) }) }),

  select: (loadId: string, quote: { shipVia: string; serviceName: string; rate: number; deliveryDate: string | null; transitDays: number }) =>
    request<Load>(`/api/loads/${encodeURIComponent(loadId)}/select`, {
      method: 'POST',
      body: JSON.stringify(quote),
    }),

  clearSelection: (loadId: string) =>
    request<Load>(`/api/loads/${encodeURIComponent(loadId)}/select`, { method: 'DELETE' }),

  reset: () => request<{ cleared: number; durable: boolean }>('/api/reset', { method: 'POST' }),

  book: (loadId: string, quote: { shipVia: string; serviceName: string; rate: number; deliveryDate: string | null; transitDays: number }) =>
    request<BookResult>(`/api/loads/${encodeURIComponent(loadId)}/book`, {
      method: 'POST',
      body: JSON.stringify(quote),
    }),
};

export const api = STATIC_BUILD ? staticApi : networkApi;
